// ============================================
// COMPONENT - Protected Route (con Session Gate integrado)
// ============================================

import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore, useInitAuth, useSession, useUIStore, useMisPermisos } from '@almacenes/core';
import { LoadingPage } from '@/pages/LoadingPage';

type UserRole = 'propietario' | 'vendedor';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Si es true, solo verifica autenticación sin cargar sesión ni aplicar gates
   * Útil para páginas de encuesta o suscripción que no necesitan sesión completa
   */
  skipSessionGate?: boolean;
  /**
   * Rol mínimo requerido para acceder a la ruta.
   * Si el usuario no tiene el rol, se redirige a /ventas.
   */
  requiredRole?: UserRole;
  /**
   * Con requiredRole="propietario", permite entrar también a un vendedor que
   * tenga al menos un permiso otorgado (el gating fino de qué puede hacer
   * dentro de la página ya lo resuelve la página misma vía useMisPermisos/
   * usePuedeCaja). Sin esto, la ruta queda exclusiva del propietario.
   */
  allowWithAnyPermission?: boolean;
}

/**
 * Componente que combina protección de ruta y control de sesión
 * 1. Restaura sesión desde refresh token si es necesario
 * 2. Verifica autenticación
 * 3. Carga datos de sesión completos
 * 4. Aplica gates (encuesta, suscripción)
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  skipSessionGate = false,
  requiredRole,
  allowWithAnyPermission = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // ========== TODOS LOS HOOKS AL INICIO (antes de cualquier return) ==========
  
  // 1. Restaurar sesión desde refresh token
  const { isInitialized } = useInitAuth();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { session: cachedSession, logout } = useAuthStore();
  
  // 2. Cargar datos de sesión completos (solo si está autenticado y no skip)
  const { data: session, isLoading: isLoadingSession, isError: isSessionError } = useSession(!skipSessionGate);
  const addToast = useUIStore((state) => state.addToast);

  // Solo se consulta cuando la ruta lo necesita: requiere rol propietario pero
  // acepta un vendedor con algún permiso otorgado. Se evita disparar la
  // consulta para el propio propietario (rolDesdeSesion === true) — su rol ya
  // alcanza y no debe esperar esta carga extra para entrar a la página.
  const rolDesdeSesion = session?.user?.esPropietario ?? cachedSession?.user?.esPropietario;
  const necesitaChequearPermisos =
    allowWithAnyPermission && requiredRole === 'propietario' && rolDesdeSesion === false;
  // isPending (no isLoading): con enabled recién pasando a true en este mismo
  // render, isLoading todavía puede ser false (fetch ni empezó) — isPending
  // cubre también ese instante, evitando decidir accessDenied sin datos.
  const { data: misPermisos, isPending: isPendingPermisos } = useMisPermisos(necesitaChequearPermisos);

  const [showOfflineMode, setShowOfflineMode] = useState(false);

  // 3. Después de 5 segundos de loading, si hay caché, mostrar modo offline
  useEffect(() => {
    if (!skipSessionGate && isLoadingSession && cachedSession) {
      const timer = setTimeout(() => {
        setShowOfflineMode(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [skipSessionGate, isLoadingSession, cachedSession]);

  // 4. Después de 8 segundos sin respuesta y sin caché, redirigir al login
  useEffect(() => {
    if (!skipSessionGate && (isLoadingSession || isSessionError) && !cachedSession) {
      const timer = setTimeout(() => {
        console.warn('Backend no disponible y sin caché, redirigiendo al login');
        logout();
        if ((window as any).electronAPI) {
          window.location.hash = '/auth/login';
        } else {
          window.location.href = '/auth/login';
        }
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [skipSessionGate, isLoadingSession, isSessionError, cachedSession, logout]);

  // 5. Aplicar gates de sesión
  const effectiveSession = session || (showOfflineMode ? cachedSession : null);

  // 6. Notificar acceso denegado por rol
  const tienePermisoAlternativo = necesitaChequearPermisos && (misPermisos?.length ?? 0) > 0;
  const esperandoPermisos = necesitaChequearPermisos && isPendingPermisos;
  const accessDenied = !!(requiredRole && effectiveSession && !esperandoPermisos &&
    (effectiveSession.user?.esPropietario ? 'propietario' : 'vendedor') !== requiredRole &&
    !tienePermisoAlternativo);

  useEffect(() => {
    if (!accessDenied) return;
    const id = setTimeout(() => {
      addToast('error', 'No tienes permisos para acceder a esa sección');
    }, 0);
    return () => clearTimeout(id);
  }, [accessDenied]); // eslint-disable-line react-hooks/exhaustive-deps
  
  useEffect(() => {
    if (skipSessionGate) return;
    if (isLoadingSession && !showOfflineMode) return;
    if (isSessionError && !effectiveSession) return;
    if (!effectiveSession) return;

    /*console.log('[ProtectedRoute] Verificando gates con sesión:', {
      mustAnswerSurvey: effectiveSession.mustAnswerSurvey,
      encuestaId: effectiveSession.encuestaId,
      hasFreemiumAccess: effectiveSession.hasFreemiumAccess,
      hasPremiumAccess: effectiveSession.hasPremiumAccess,
      usingCached: !session && showOfflineMode
    });*/

    // Gate 1: Encuesta obligatoria
    if (effectiveSession.mustAnswerSurvey && effectiveSession.encuestaId) {
      //console.log('[ProtectedRoute] ❌ Bloqueado por encuesta obligatoria, redirigiendo...');
      navigate(`/encuestas/${effectiveSession.encuestaId}`, { replace: true });
      return;
    }

    // Gate 2: Sin acceso (ni freemium ni premium)
    if (!effectiveSession.hasFreemiumAccess && !effectiveSession.hasPremiumAccess) {
      //console.log('[ProtectedRoute] ❌ Sin acceso, redirigiendo a suscripción...');
      navigate('/suscripcion', { replace: true });
      return;
    }
    
    //console.log('[ProtectedRoute] ✅ Todos los gates pasados');
  }, [skipSessionGate, effectiveSession, isLoadingSession, isSessionError, navigate, showOfflineMode]);

  /*console.log('[ProtectedRoute] Estado:', { 
    isInitialized, 
    isAuthenticated, 
    skipSessionGate, 
    isLoadingSession,
    hasSession: !!session,
    hasEffectiveSession: !!effectiveSession
  });*/

  // ========== LÓGICA DE RENDERIZADO CONDICIONAL ==========

  // PASO 1: Esperar restauración de sesión desde refresh token
  if (!isInitialized) {
    //console.log('[ProtectedRoute] Esperando inicialización...');
    return <LoadingPage />;
  }

  // PASO 2: Verificar autenticación
  if (!isAuthenticated) {
    //console.log('[ProtectedRoute] No autenticado, redirigiendo a login');
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // PASO 3: Si skip session gate, renderizar directamente
  if (skipSessionGate) {
    //console.log('[ProtectedRoute] Skip session gate, renderizando');
    return <>{children}</>;
  }

  // PASO 4: Esperar carga completa de datos de sesión
  // Esto es CRÍTICO para que los gates (encuesta, suscripción) funcionen correctamente
  if (!effectiveSession) {
    //console.log('[ProtectedRoute] Esperando datos de sesión...');
    return <LoadingPage />;
  }

  // PASO 4b: si el rol no alcanza, esperar la respuesta de mis-permisos antes
  // de decidir accessDenied (evita un flash de "acceso denegado" prematuro)
  if (esperandoPermisos) {
    return <LoadingPage />;
  }

  // PASO 5: Verificar rol requerido
  if (accessDenied) {
    return <Navigate to="/dashboard" replace />;
  }

  // PASO 6: Renderizar contenido (los gates se manejan en useEffect)
  //console.log('[ProtectedRoute] Sesión cargada, renderizando contenido');
  return <>{children}</>;
};
